import React, { Component } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import ErrorLabel from '../../components/ErrorLabel/ErrorLabel';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Alert from "../../components/Alert/Alert";
import { faEnvelope, faUser, faLock } from '@fortawesome/free-solid-svg-icons';
import { Link } from "react-router-dom";
import axios from 'axios';
import css from '../Login/Login.module.scss'; // n.b. we're reusing Login styles
import { EMAIL_REGEX } from '../../constants';


/**
 * @class
 */
const CreateLogin = class extends Component {
    // ---------- class fields: ----------
    source = axios.CancelToken.source();
    _isMounted = false;
    postRequestCreateLogin = {};

    // ---------- state: ----------
    state = {
        shouldShowPasswordFields: false,
        shouldShowAlert: false,
        alertMessage: ''
    };

    // ---------- methods: ----------
    /**
     * @method
     * @methodOf CreateLogin
     *
     * Function to show the Alert component and hide it after 5.5 seconds.
     *
     * @param message {string}: The text we want the alert message to show.
     */
    showAlertMessage = (message) => {
        // show error alert:
        this.setState({
            shouldShowAlert: true,
            alertMessage: message
        });
        setTimeout(() => {
            // condition to guard against setting state when component is unmounted:
            if (this._isMounted) {
                this.setState({shouldShowAlert: false});
            }
        }, 5500);
    };

    // ---------- life cycle methods: ----------
    componentDidMount() {
        this._isMounted = true;
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.source.cancel();
    }

    render() {
        return (
            <div className="App App--Login">
                <Alert showAlert={this.state.shouldShowAlert}>{this.state.alertMessage}</Alert>
                <div className={css.Login}>
                    <img className={css.Login__Logo} src="/assets/logo-V3.png" alt="V3 logo" />
                    <h3 className="text-white mt-5 pt-4">New User</h3>

                    <div className={this.state.shouldShowPasswordFields ? 'd-none' : ''}>
                        <Formik
                            initialValues={{
                                firstName: '',
                                lastName: '',
                                email: '',
                            }}

                            validate={
                                (values)=> {
                                    const errors = {};

                                    if(!values.firstName.trim()) {
                                        errors.firstName = 'Please Enter a First Name';
                                    }

                                    if(!values.lastName.trim()) {
                                        errors.lastName = 'Please Enter a Last Name';
                                    }

                                    if (!values.email.trim()) {
                                        errors.email = 'Please Enter An Email Address';
                                    } else if (!EMAIL_REGEX.test(values.email)) {
                                        errors.email = 'Invalid Email Address';
                                    }

                                    return errors;
                                }
                            }

                            onSubmit={
                                (values) => {
                                    const payload = {
                                        email: values.email,
                                        fn: values.firstName,
                                        ln: values.lastName
                                    };

                                    axios.post(
                                        '/user/info',
                                        payload,
                                        {
                                            cancelToken: this.source.token
                                        })
                                        .then((res) => {
                                            if (!res.data.dbLogin) {
                                                return;
                                            }

                                            const data = res.data.data[1];
                                            let role;
                                            let subrole;

                                            switch (data.Job_Department__c) {
                                                case 'Survey':
                                                    role = 'field';
                                                    subrole = 'survey';
                                                    break;
                                                case 'Installation':
                                                    role = 'field';
                                                    subrole = 'install';
                                                    break;
                                                case 'Inspection':
                                                    role = 'field';
                                                    subrole = 'inspect';
                                                    break;
                                                case 'Electrician':
                                                    role = 'field';
                                                    subrole = 'electric';
                                                    break;
                                                case 'Contractor':
                                                    role = 'field';
                                                    subrole = 'contract';
                                                    break;
                                                case 'Partners':
                                                    role = 'partner';
                                                    subrole= 'rep';
                                                    break;
                                                default:
                                                    role = 'rep';
                                                    subrole = 'rep';
                                                    break;
                                            }

                                            if (data.Job_Title__c === 'Sales Manager') {
                                                subrole = 'lead';
                                            }

                                            this.postRequestCreateLogin = {
                                                email: values.email,
                                                name: data.Name,
                                                apiId: data.Id,
                                                username: values.email,
                                                role: role,
                                                triggers: {},
                                                subrole: subrole
                                            };

                                            this.setState({shouldShowPasswordFields: true});
                                        })
                                        .catch(err => {
                                            const error = err.response.data;

                                            if (error && !error.dbLogin) {
                                                if (error.err.existUser) {
                                                    this.showAlertMessage('A user with that email address already exists. Contact your sales coordinator or try the "Forgot" password link on the login page.');
                                                } else if (error.err.multUser) {
                                                    this.showAlertMessage('Multiple contacts with this name/email combination were found. Please contact your sales coordinator to set up your account.');
                                                } else {
                                                    this.showAlertMessage('You are not currently allowed to set up a Dashboard login. Please contact your sales coordinator or department manager.');
                                                }
                                            } else {
                                                this.showAlertMessage('Could not find sales rep contact.');
                                            }
                                        });
                                }
                            }
                        >
                            <div>
                                <p className="mt-3">
                                    Enter your first name, last name, and V3 email address.
                                </p>
                                <Form className="mt-4">
                                    <div>
                                        <div className="input-with-icons-container">
                                            <FontAwesomeIcon icon={faUser} />
                                            <Field className="input" type="text" name="firstName" placeholder="First Name" />
                                        </div>
                                        <ErrorMessage name="firstName" component={ErrorLabel} />
                                    </div>

                                    <div className="mt-3">
                                        <div className="input-with-icons-container">
                                            <FontAwesomeIcon icon={faUser} />
                                            <Field className="input" type="text" name="lastName" placeholder="Last Name" />
                                        </div>
                                        <ErrorMessage name="lastName" component={ErrorLabel} />
                                    </div>

                                    <div className="mt-3">
                                        <div className="input-with-icons-container">
                                            <FontAwesomeIcon icon={faEnvelope} />
                                            <Field className="input" type="text" name="email" placeholder="Email" />
                                        </div>
                                        <ErrorMessage name="email" component={ErrorLabel} />
                                    </div>

                                    <div className="mt-4 d-flex flex-no-wrap align-items-center justify-content-between">
                                        <Link to="/login" className="link-underline">Go Back</Link>
                                        <button className={css.Login__BtnSubmit} type="submit">Check</button>
                                    </div>
                                </Form>
                            </div>
                        </Formik>
                    </div>

                    <div className={this.state.shouldShowPasswordFields ? '' : 'd-none'}>
                        <Formik
                            initialValues={{
                                password: '',
                                repeatPassword: ''
                            }}

                            validate={
                                (values)=> {
                                    const errors = {};

                                    if (!values.password.trim()) {
                                        errors.password = 'Please Enter a Password';
                                    } else if (values.password.length < 8) {
                                        errors.password = "Password Must be Minimum 8 Characters Long";
                                    }

                                    if (!values.repeatPassword.trim()) {
                                        errors.repeatPassword = 'Please Reenter Your Password';
                                    } else if (values.repeatPassword !== values.password) {
                                        errors.repeatPassword = 'Passwords Do Not Match';
                                    }

                                    return errors;
                                }
                            }

                            onSubmit={
                                (values) => {
                                    const payload = {...this.postRequestCreateLogin};
                                    payload.password = values.password;

                                    axios.post(
                                        '/user',
                                        payload,
                                        {
                                            cancelToken: this.source.token
                                        })
                                        .then((res) => {
                                            if(res.data.id) {
                                                this.showAlertMessage('Login Created Successfully.');
                                            }
                                        })
                                        .catch(err => {
                                            this.showAlertMessage('You are not currently allowed to set up a Dashboard login. Please contact your sales coordinator.');
                                        });
                                }
                            }
                        >
                            <div>
                                <p className="mt-3">
                                    Enter the password you want to use for your login.
                                </p>
                                <Form className="mt-4">
                                    <div>
                                        <div className="input-with-icons-container">
                                            <FontAwesomeIcon icon={faLock} />
                                            <Field className="input" type="text" name="password" placeholder="Password" />
                                        </div>
                                        <ErrorMessage name="password" component={ErrorLabel} />
                                    </div>

                                    <div className="mt-3">
                                        <div className="input-with-icons-container">
                                            <FontAwesomeIcon icon={faLock} />
                                            <Field className="input" type="text" name="repeatPassword" placeholder="Repeat Password" />
                                        </div>
                                        <ErrorMessage name="repeatPassword" component={ErrorLabel} />
                                    </div>

                                    <div className="mt-4 d-flex flex-no-wrap align-items-center justify-content-between">
                                        <Link to="/login" className="link-underline">Go Back</Link>
                                        <button className={css.Login__BtnSubmit} type="submit">Create Login</button>
                                    </div>
                                </Form>
                            </div>
                        </Formik>
                    </div>

                </div>
            </div>
        );
    }
};

export default CreateLogin;
